// Calendar dates are inclusive and use the configured business time zone, not the shopper's.
function announcementBusinessDate(now, timeZone) {
  let formatter;
  try { formatter = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }); }
  catch { formatter = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit' }); }
  const parts = Object.fromEntries(formatter.formatToParts(now).map(part => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function announcementIsCurrent(start, end, today) {
  const valid = value => !value || (/^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`)) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value);
  return valid(start) && valid(end) && (!start || today >= start) && (!end || today <= end);
}

class DaisyAnnouncementBar extends HTMLElement {
  connectedCallback() {
    queueMicrotask(() => { if (this.isConnected && !this.controller) this.init(); });
  }

  init() {
    this.group = this.querySelector('[data-announcement-group]');
    this.track = this.querySelector('[data-announcement-track]');
    this.viewport = this.querySelector('[data-announcement-viewport]');
    if (!this.group || !this.track || !this.viewport) return;
    this.messages = [...this.group.querySelectorAll('[data-announcement-message]')];
    this.activeMessages = null;
    this.controls = this.querySelector('[data-announcement-controls]');
    this.toggle = this.querySelector('[data-announcement-toggle]');
    this.next = this.querySelector('[data-announcement-next]');
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.mobile = window.matchMedia('(max-width: 1024px)');
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.userPaused = false;
    this.hovered = false;
    this.focused = false;
    this.keyboardFocused = false;
    this.editorSelected = false;
    this.activeIndex = 0;
    this.onResize = () => {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => this.configure());
    };
    this.mobile.addEventListener('change', this.onResize, options);
    this.reduced.addEventListener('change', this.onResize, options);
    window.addEventListener('resize', this.onResize, options);
    document.addEventListener('visibilitychange', () => { this.refreshMessages(); this.syncPlayback(); }, options);
    this.addEventListener('mouseenter', () => { this.hovered = this.dataset.pauseOnHover !== 'false'; this.syncPlayback(); }, options);
    this.addEventListener('mouseleave', () => { this.hovered = false; this.syncPlayback(); }, options);
    this.addEventListener('focusin', event => {
      this.focused = true;
      this.keyboardFocused = event.target.matches(':focus-visible');
      // Put original links in view for keyboard users while the desktop ticker is paused.
      if (!this.mobile.matches && this.keyboardFocused && event.target.closest('[data-announcement-message]')) this.configure();
      this.syncPlayback();
    }, options);
    this.addEventListener('focusout', () => queueMicrotask(() => {
      this.focused = this.contains(document.activeElement);
      if (!this.focused) { this.resumeRequested = false; this.keyboardFocused = false; this.configure(); }
      this.syncPlayback();
    }), options);
    this.toggle?.addEventListener('click', () => {
      this.userPaused = !this.userPaused;
      this.resumeRequested = !this.userPaused;
      this.syncPlayback();
    }, options);
    this.next?.addEventListener('click', () => {
      this.showMessage(this.activeIndex + 1, true);
      this.syncPlayback();
    }, options);
    this.addEventListener('keydown', event => {
      if (this.dataset.mode !== 'rotate' || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.showMessage(this.activeIndex + (event.key === 'ArrowRight' ? 1 : -1), true);
    }, options);
    document.addEventListener('shopify:block:select', event => {
      if (String(event.detail.sectionId) !== this.dataset.sectionId) return;
      this.editorSelected = true;
      this.configure();
      const index = this.activeMessages.findIndex(message => message.dataset.blockId === event.detail.blockId);
      if (index >= 0) this.showMessage(index);
      this.syncPlayback();
    }, options);
    document.addEventListener('shopify:block:deselect', event => {
      if (String(event.detail.sectionId) !== this.dataset.sectionId) return;
      this.editorSelected = false;
      this.configure();
    }, options);
    this.refreshMessages();
    document.fonts?.ready.then(() => { if (this.controller) this.configure(); });
    this.scheduleTimer = setInterval(() => this.refreshMessages(), 30000);
  }

  refreshMessages() {
    const today = announcementBusinessDate(new Date(), this.dataset.timeZone || 'America/Chicago');
    const previous = this.activeMessages?.map(message => message.dataset.blockId).join('|');
    const current = this.activeMessages?.[this.activeIndex];
    this.activeMessages = this.messages.filter(message => this.dataset.designMode === 'true' || announcementIsCurrent(message.dataset.startDate, message.dataset.endDate, today));
    this.messages.forEach(message => { message.hidden = !this.activeMessages.includes(message); });
    this.hidden = this.activeMessages.length === 0;
    const next = this.activeMessages.map(message => message.dataset.blockId).join('|');
    if (previous !== next) {
      this.activeIndex = Math.max(0, this.activeMessages.indexOf(current));
      this.configure();
    }
  }

  configure() {
    if (!this.controller || !this.activeMessages) return;
    clearTimeout(this.rotationTimer);
    this.querySelectorAll('[data-announcement-clone]').forEach(clone => clone.remove());
    const requested = this.mobile.matches ? this.dataset.mobileMode : this.dataset.desktopMode;
    const staticMode = this.reduced.matches || this.editorSelected || (!this.mobile.matches && this.keyboardFocused);
    let mode = staticMode || this.activeMessages.length <= 1 ? 'static' : requested;
    if (!['scroll', 'rotate'].includes(mode)) mode = 'static';
    this.dataset.mode = mode;
    this.activeMessages.forEach(message => {
      message.removeAttribute('aria-hidden');
      message.querySelector('a')?.removeAttribute('tabindex');
    });
    if (mode === 'scroll') {
      const width = this.group.getBoundingClientRect().width;
      if (width > 0) {
        const copies = Math.min(50, Math.ceil(this.viewport.clientWidth / width) + 1);
        for (let index = 0; index < copies; index++) {
          const clone = this.group.cloneNode(true);
          clone.removeAttribute('data-announcement-group');
          clone.setAttribute('data-announcement-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('a').forEach(link => link.setAttribute('tabindex', '-1'));
          clone.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
          clone.querySelectorAll('[data-shopify-editor-block]').forEach(node => node.removeAttribute('data-shopify-editor-block'));
          this.track.append(clone);
        }
        const speed = Math.max(10, Number(this.dataset.scrollSpeed) || 30);
        this.style.setProperty('--announcement-shift', `${-width}px`);
        this.style.setProperty('--announcement-duration', `${width / speed}s`);
      }
    }
    this.showMessage(this.activeIndex);
    if (this.controls) this.controls.hidden = mode === 'static' || this.activeMessages.length <= 1;
    if (this.next) this.next.hidden = mode !== 'rotate';
    this.classList.add('is-ready');
    this.syncPlayback();
  }

  showMessage(index, announce = false) {
    const count = this.activeMessages.length;
    if (!count) return;
    this.activeIndex = ((index % count) + count) % count;
    this.activeMessages.forEach((message, position) => {
      const inactive = this.dataset.mode === 'rotate' && position !== this.activeIndex;
      message.classList.toggle('is-active', position === this.activeIndex);
      if (inactive) message.setAttribute('aria-hidden', 'true');
      else message.removeAttribute('aria-hidden');
      const link = message.querySelector('a');
      if (link) { if (inactive) link.setAttribute('tabindex', '-1'); else link.removeAttribute('tabindex'); }
    });
    if (announce) {
      const status = this.querySelector('[data-announcement-status]');
      if (status) status.textContent = this.activeMessages[this.activeIndex].textContent.trim();
    }
  }

  syncPlayback() {
    clearTimeout(this.rotationTimer);
    const paused = this.userPaused || this.hovered || (this.focused && !this.resumeRequested) || document.hidden || this.editorSelected;
    this.classList.toggle('is-paused', paused);
    this.classList.toggle('is-user-paused', this.userPaused);
    if (this.toggle) {
      this.toggle.setAttribute('aria-pressed', String(this.userPaused));
      this.toggle.setAttribute('aria-label', this.userPaused ? this.dataset.resumeLabel : this.dataset.pauseLabel);
    }
    if (!paused && this.dataset.mode === 'rotate' && this.activeMessages?.length > 1) {
      const delay = Math.max(3000, (Number(this.dataset.rotationInterval) || 7) * 1000);
      this.rotationTimer = setTimeout(() => { this.showMessage(this.activeIndex + 1); this.syncPlayback(); }, delay);
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    clearTimeout(this.rotationTimer);
    clearInterval(this.scheduleTimer);
    cancelAnimationFrame(this.resizeFrame);
    this.querySelectorAll('[data-announcement-clone]').forEach(clone => clone.remove());
  }
}

if (!customElements.get('daisy-announcement-bar')) customElements.define('daisy-announcement-bar', DaisyAnnouncementBar);
